import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import CustomerHeader from "../../components/CustomerHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Gift, Users, Copy, Share2, Sparkles, Ticket, Send, QrCode, Store, TrendingUp, Rocket, Plus, Compass, Swords } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";

const AMOUNTS = [10, 20, 30, 50];

export default function Growth() {
  const [ref, setRef] = useState(null);
  const [cards, setCards] = useState({ purchased: [], redeemed: [] });
  const [amount, setAmount] = useState(20);
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [redeemCode, setRedeemCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [growth, setGrowth] = useState(null);
  const [pOpen, setPOpen] = useState(false);
  const EMPTY_P = { name: "", comune: "", category: "", address: "", phone: "", email: "", notes: "", site: "" };
  const [pForm, setPForm] = useState(EMPTY_P);
  const [cityComune, setCityComune] = useState(localStorage.getItem("fuma_comune") || "");
  const [city, setCity] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [params] = useSearchParams();

  useEffect(() => {
    if (!cityComune) { setCity(null); return; }
    localStorage.setItem("fuma_comune", cityComune);
    const t = setTimeout(() => {
      api.get(`/growth/city?comune=${encodeURIComponent(cityComune)}`).then(({ data }) => setCity(data.available ? data : null)).catch(() => setCity(null));
    }, 400);
    return () => clearTimeout(t);
  }, [cityComune]);

  const load = () => {
    api.get("/referral/me").then(({ data }) => setRef(data)).catch(() => {});
    api.get("/giftcards/me").then(({ data }) => setCards(data)).catch(() => {});
    api.get("/growth/summary").then(({ data }) => setGrowth(data)).catch(() => {});
    api.get("/growth/territory-challenges").then(({ data }) => setChallenges(data.challenges || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const gs = params.get("gift_session");
    if (!gs) return;
    api.get(`/payments/status/${gs}`).then(({ data }) => {
      if (data.payment_status === "paid") { toast.success("🎁 Gift card acquistata! Trovi il codice qui sotto."); load(); }
    }).catch(() => {});
  }, [params]);

  const shareUrl = ref ? `${window.location.origin}/login?ref=${ref.code}` : "";
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiato!"); };
  const share = async () => {
    const text = `Ordina con Fuma c'anduma e ricevi crediti! Usa il mio codice ${ref.code}`;
    if (navigator.share) { try { await navigator.share({ title: "Fuma c'anduma", text, url: shareUrl }); } catch { /* */ } }
    else copy(`${text} — ${shareUrl}`);
  };

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/giftcards/purchase", { amount, recipient_email: recipient, message, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/giftcards/redeem", { code: redeemCode });
      toast.success(`+${data.credits} crediti aggiunti al tuo saldo!`);
      setRedeemCode(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const bizShareUrl = growth ? `${window.location.origin}/candidatura?ref=${growth.code}` : "";
  const shareBusiness = async () => {
    const text = growth.share_business;
    if (navigator.share) { try { await navigator.share({ title: "Fuma c'anduma", text, url: bizShareUrl }); } catch { /* */ } }
    else copy(`${text} — ${bizShareUrl}`);
  };
  const proposeBusiness = async () => {
    if (!pForm.name || !pForm.comune) { toast.error("Nome attività e comune sono obbligatori"); return; }
    try {
      const { data } = await api.post("/growth/propose-business", pForm);
      toast.success(data.already_voted ? "Avevi già proposto questa attività!" : "Grazie! Attività proposta a Fuma 🙌");
      setPOpen(false); setPForm(EMPTY_P); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const qr = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=0A0A0C&color=00E676&data=${encodeURIComponent(data)}`;

  return (
    <div className="min-h-screen bg-[#0A0A0C] grain">
      <CustomerHeader />
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="growth-page">
        <div>
          <h1 className="text-2xl font-display font-black">Fai crescere Fuma</h1>
          <p className="text-sm text-zinc-500">Più siamo, più vantaggi sblocchiamo. Porta persone e attività della tua zona.</p>
        </div>

        {/* Growth Engine hero */}
        <div className="rounded-2xl p-6 border border-[#FFD700]/30 bg-gradient-to-br from-[#FFD700]/10 to-[#131316]" data-testid="growth-hero">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FFD700]"><Rocket className="h-4 w-4" /> Il tuo impatto su Fuma</div>
            {growth?.scout?.level && (
              <span data-testid="scout-level" className="text-xs bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40 rounded-full px-3 py-1 font-semibold flex items-center gap-1"><Compass className="h-3.5 w-3.5" /> {growth.scout.level.name}</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-center">
            <div><div className="text-2xl font-black" data-testid="impact-invited">{growth?.invited ?? 0}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Persone portate</div></div>
            <div><div className="text-2xl font-black">{growth?.businesses_entered ?? 0}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Attività portate</div></div>
            <div><div className="text-2xl font-black text-[#FFD700]">{growth?.growth_points ?? 0}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Punti crescita</div></div>
            <div><div className="text-2xl font-black text-[#00E676]" data-testid="impact-econ">€{Number(growth?.economic_value ?? 0).toLocaleString("it-IT")}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Vendite generate</div></div>
          </div>
          {growth?.scout?.next && (
            <p className="text-xs text-zinc-400 mt-3 text-center">Ancora {growth.scout.next.threshold - (growth.scout.valid_businesses || 0)} attività valide per diventare <b className="text-[#FFD700]">{growth.scout.next.name}</b>.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <Button data-testid="growth-bring-person" onClick={share} disabled={!ref} className="bg-[#00E676] text-black rounded-full font-semibold"><Users className="h-4 w-4 mr-1" /> Porta una persona</Button>
            <Dialog open={pOpen} onOpenChange={setPOpen}>
              <DialogTrigger asChild><Button data-testid="growth-bring-business" variant="outline" className="rounded-full border-[#FFD700]/40 text-[#FFD700]"><Store className="h-4 w-4 mr-1" /> Porta un'attività</Button></DialogTrigger>
              <DialogContent className="bg-[#141416] border-white/10 max-w-lg">
                <DialogHeader><DialogTitle>Proponi un'attività su Fuma</DialogTitle></DialogHeader>
                <p className="text-xs text-zinc-500 -mt-2">Basta nome e comune. Telefono ed email sono opzionali.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <div className="sm:col-span-2"><Label className="text-xs">Nome attività *</Label><Input data-testid="propose-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
                  <div><Label className="text-xs">Comune *</Label><Input data-testid="propose-comune" value={pForm.comune} onChange={(e) => setPForm({ ...pForm, comune: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
                  <div><Label className="text-xs">Categoria</Label><Input value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
                  <div><Label className="text-xs">Indirizzo (opz.)</Label><Input value={pForm.address} onChange={(e) => setPForm({ ...pForm, address: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
                  <div><Label className="text-xs">Telefono (opz.)</Label><Input value={pForm.phone} onChange={(e) => setPForm({ ...pForm, phone: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs">Note (opz.)</Label><Textarea value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button data-testid="propose-submit" onClick={proposeBusiness} className="bg-[#00E676] text-black rounded-full flex-1 font-semibold">Proponi attività</Button>
                  <Button variant="outline" onClick={shareBusiness} className="rounded-full border-white/20"><Share2 className="h-4 w-4 mr-1" /> Invita tramite link</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Progressione territoriale (spec 12-14) */}
        <div className="rounded-2xl p-6 border border-[#7DF9FF]/25 bg-gradient-to-br from-[#7DF9FF]/8 to-[#131316]" data-testid="city-progress">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#7DF9FF]"><TrendingUp className="h-4 w-4" /> La tua città su Fuma</div>
            <Input data-testid="city-input" value={cityComune} onChange={(e) => setCityComune(e.target.value)} placeholder="Il tuo comune (es. Ciriè)" className="bg-[#0A0A0C] border-white/10 h-9 max-w-[220px]" />
          </div>
          {city ? (
            <div className="mt-4">
              <div className="flex items-end justify-between">
                <div className="font-display font-black text-2xl">{city.comune} <span className="text-[#7DF9FF]">Livello {city.level}</span></div>
                <div className="text-sm text-zinc-400">{city.progress_pct}%</div>
              </div>
              <div className="h-3 rounded-full bg-white/10 mt-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#7DF9FF] to-[#00E676] rounded-full transition-all" style={{ width: `${Math.min(100, city.progress_pct)}%` }} data-testid="city-bar" />
              </div>
              <p className="text-xs text-zinc-400 mt-2">Mancano <b className="text-[#7DF9FF]">{city.missing_businesses} attività</b> al Livello {city.level + 1}. {city.active_businesses} attività attive · {city.active_users} utenti attivi.{city.reward ? ` Reward città: ${city.reward.label || "sbloccato"}.` : ""}</p>
            </div>
          ) : cityComune ? <p className="text-xs text-zinc-500 mt-3">Fuma non è ancora attiva a {cityComune}. Porta la prima attività e falla partire!</p>
            : <p className="text-xs text-zinc-500 mt-3">Inserisci il tuo comune per vedere il livello e quanto manca al prossimo.</p>}
        </div>

        {/* Sfide territoriali — Città vs Città */}
        {challenges.length > 0 && (
          <div className="rounded-2xl p-6 border border-[#7DF9FF]/25 bg-gradient-to-br from-[#7DF9FF]/8 to-[#131316]" data-testid="territory-challenges">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#7DF9FF] mb-3"><Swords className="h-4 w-4" /> Sfide del territorio</div>
            <div className="space-y-4">
              {challenges.map((c) => {
                const max = Math.max(1, ...(c.standings || []).map((s) => s.value));
                return (
                  <div key={c.id} data-testid={`tc-${c.id}`} className="border-t border-white/5 pt-3 first:border-0 first:pt-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">{c.title}
                      <span className="text-zinc-500 text-xs">· obiettivo {c.target} {c.metric === "gmv" ? "€ GMV" : c.metric === "new_users" ? "nuovi clienti" : "ordini"}{c.ended ? " · CONCLUSA" : ""}</span></div>
                    <div className="space-y-1.5 mt-2">
                      {(c.standings || []).map((s, i) => (
                        <div key={s.comune} className="flex items-center gap-2">
                          <span className={`text-xs w-28 shrink-0 truncate ${i === 0 && s.value > 0 ? "text-[#FFD700] font-semibold" : "text-zinc-300"}`}>{i === 0 && s.value > 0 ? "🏆 " : ""}{s.comune}</span>
                          <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#7DF9FF] to-[#00E676]" style={{ width: `${(s.value / max) * 100}%` }} /></div>
                          <span className="text-xs text-zinc-400 w-10 text-right">{s.value}</span>
                        </div>
                      ))}
                    </div>
                    {c.xp_reward > 0 && <p className="text-[11px] text-zinc-500 mt-2">Il comune vincitore riceve +{c.xp_reward} XP per chi partecipa{c.city_reward ? " + un premio territoriale" : ""}.</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Referral con QR */}
        <div className="rounded-2xl p-6 border border-[#00E676]/25 bg-gradient-to-br from-[#00E676]/10 to-[#131316]" data-testid="referral-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#00E676] mb-2"><Users className="h-4 w-4" /> Invita un amico</div>
          <p className="text-sm text-zinc-300 mb-4">Il tuo amico riceve <b>{ref?.referee_reward ?? 50} crediti</b> di benvenuto e tu guadagni <b>{ref?.referrer_reward ?? 100} crediti</b> quando completa il suo primo ordine.</p>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 rounded-xl bg-[#0A0A0C] border border-white/10 px-4 py-3 font-mono text-lg tracking-widest text-[#00E676]" data-testid="referral-code">{ref?.code || "…"}</div>
            <Button variant="secondary" data-testid="referral-copy" onClick={() => copy(ref.code)} disabled={!ref}><Copy className="h-4 w-4" /></Button>
            <Button className="bg-[#00E676] text-black" data-testid="referral-share" onClick={share} disabled={!ref}><Share2 className="h-4 w-4 mr-1" /> Invita</Button>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-2xl font-bold">{ref?.invited ?? 0}</div><div className="text-xs text-zinc-500">Invitati</div></div>
            <div><div className="text-2xl font-bold text-[#00E676]">{ref?.converted ?? 0}</div><div className="text-xs text-zinc-500">Attivati</div></div>
            <div><div className="text-2xl font-bold text-amber-300">{growth?.growth_points ?? 0}</div><div className="text-xs text-zinc-500">Punti guadagnati</div></div>
          </div>
          {growth?.code && (
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
              <img src={qr(shareUrl)} alt="QR referral" className="h-24 w-24 rounded-lg bg-[#0A0A0C] p-1" data-testid="referral-qr" />
              <div className="text-xs text-zinc-400">Inquadra il QR o condividi il link per invitare. Guadagni punti quando l'invitato effettua il 1°, 3° e 5° ordine{growth?.is_plus ? " (x2 con Fuma+)" : ""}.</div>
            </div>
          )}
        </div>

        {/* Gift card acquisto */}
        <div className="rounded-2xl p-6 border border-white/10 bg-[#1C1C1F]" data-testid="giftcard-buy-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 mb-3"><Gift className="h-4 w-4 text-[#00E676]" /> Regala una Gift Card</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {AMOUNTS.map((a) => (
              <button key={a} data-testid={`gift-amount-${a}`} onClick={() => setAmount(a)} className={`px-4 py-2 rounded-xl border text-sm font-semibold ${amount === a ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-white/10 text-zinc-400"}`}>{a}€</button>
            ))}
            <Input type="number" min="5" max="200" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-24 bg-[#0A0A0C] border-white/10" data-testid="gift-amount-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div><Label className="text-xs text-zinc-400">Email destinatario (opzionale)</Label><Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="amico@email.it" className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="gift-recipient" /></div>
            <div><Label className="text-xs text-zinc-400">Messaggio (opzionale)</Label><Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Buon compleanno!" className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="gift-message" /></div>
          </div>
          <Button className="bg-[#00E676] text-black w-full" data-testid="gift-buy" disabled={busy || !(amount >= 5 && amount <= 200)} onClick={buy}><Sparkles className="h-4 w-4 mr-1" /> Acquista gift card {amount}€</Button>
        </div>

        {/* Gift card riscatto */}
        <div className="rounded-2xl p-6 border border-white/10 bg-[#1C1C1F]" data-testid="giftcard-redeem-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 mb-3"><Ticket className="h-4 w-4 text-[#7DF9FF]" /> Riscatta una Gift Card</div>
          <div className="flex gap-2">
            <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="GIFT-XXXXXXXXXX" className="bg-[#0A0A0C] border-white/10 font-mono" data-testid="redeem-input" />
            <Button className="bg-[#7DF9FF] text-black" data-testid="redeem-btn" disabled={busy || !redeemCode} onClick={redeem}><Send className="h-4 w-4 mr-1" /> Riscatta</Button>
          </div>
        </div>

        {/* Storico */}
        {(cards.purchased.length > 0 || cards.redeemed.length > 0) && (
          <div className="rounded-2xl p-6 border border-white/10 bg-[#1C1C1F]" data-testid="giftcard-history">
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-3">Le tue gift card</div>
            {cards.purchased.map((g, i) => (
              <div key={`b${i}`} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                <span>Acquistata <b>{Number(g.amount).toFixed(2)}€</b> {g.recipient_email ? `→ ${g.recipient_email}` : ""}</span>
                <span className="font-mono text-[#00E676]">{g.code || (g.status === "redeemed" ? "riscattata" : g.status)}</span>
              </div>
            ))}
            {cards.redeemed.map((g, i) => (
              <div key={`r${i}`} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                <span>Riscattata <b>{Number(g.amount).toFixed(2)}€</b></span>
                <span className="text-zinc-500">{String(g.redeemed_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
